import { useState } from 'react'
import { Alert, Button, Card, Container, Form, Spinner } from 'react-bootstrap'
import { Trash2 } from 'lucide-react'
import { FULL_APPLICATION_DATA_CONFIRM_PHRASE, postFullDataReset } from '../services/api'

export default function AdminDataReset() {
  const [scope, setScope] = useState<'runtime' | 'full'>('runtime')
  const [clearHangfire, setClearHangfire] = useState(true)
  const [confirmPhrase, setConfirmPhrase] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const runtimeHelp =
    'Removes distributors, targets, jobs, findings, audit log (runtime), evidence — keeps products, PIM, rules and users.'
  const fullHelp =
    'Deletes products, comparisons, canonical terms, distributors, findings, checks, term watches, support tickets, approvals, master terms library, PIM catalogue rows, … Keeps divisions, business entities and user accounts.'

  const handleReset = async () => {
    setMessage(null)
    setError(null)
    if (scope === 'full' && confirmPhrase.trim() !== FULL_APPLICATION_DATA_CONFIRM_PHRASE) {
      setError(`Type the confirmation phrase exactly: ${FULL_APPLICATION_DATA_CONFIRM_PHRASE}`)
      return
    }
    setBusy(true)
    try {
      const res = await postFullDataReset({
        scope,
        clearHangfire,
        confirmationPhrase: scope === 'full' ? confirmPhrase.trim() : undefined,
      })
      setMessage(
        res.message +
          (res.defaultPimCategoriesReseeded ? ' Default master data-group codes were recreated.' : '') +
          (res.clearedHangfire ? ' Hangfire queues were cleared.' : ''),
      )
      setConfirmPhrase('')
      window.setTimeout(() => {
        window.location.reload()
      }, 900)
    } catch (e: unknown) {
      let msg = 'Reset failed.'
      if (typeof e === 'object' && e !== null && 'response' in e) {
        const ax = e as { response?: { status?: number; data?: { error?: string; message?: string; hint?: string } } }
        const d = ax.response?.data
        msg = [d?.error, d?.message, d?.hint].filter(Boolean).join(' — ') || msg
      } else if (e instanceof Error) msg = e.message
      setError(msg)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Container className="py-4" style={{ maxWidth: 720 }}>
      <div className="d-flex align-items-start gap-3 mb-4">
        <div className="rounded-circle bg-danger bg-opacity-10 p-3 text-danger">
          <Trash2 size={28} aria-hidden />
        </div>
        <div>
          <h1 className="h4 mb-1">Reset application data</h1>
          <p className="text-muted mb-0 small">
            Master admin only — for QA and demos — so you can walk through onboarding from scratch. Nothing here runs unless you authenticate as MasterAdmin.
          </p>
        </div>
      </div>

      <Alert variant="warning" className="small">
        <strong>Production:</strong> the API rejects this unless <code className="small">Admin:AllowDangerousMaintenance=true</code> is set alongside a production host env.
      </Alert>

      {error && <Alert variant="danger">{error}</Alert>}
      {message && (
        <Alert variant="success" className="small">
          {message} Reloading the app…
        </Alert>
      )}

      <Card className="shadow-sm mb-4">
        <Card.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">What to clear</Form.Label>
              <Form.Check
                type="radio"
                name="scope"
                id="scope-runtime"
                label={<span>Runtime / job data — {runtimeHelp}</span>}
                checked={scope === 'runtime'}
                disabled={busy}
                onChange={() => setScope('runtime')}
              />
              <Form.Check
                className="mt-2"
                type="radio"
                name="scope"
                id="scope-full"
                label={<span>Full wipe (except organisation &amp; users) — {fullHelp}</span>}
                checked={scope === 'full'}
                disabled={busy}
                onChange={() => setScope('full')}
              />
            </Form.Group>

            <Form.Check
              id="chk-hangfire"
              type="checkbox"
              className="mb-3"
              label="Clear Hangfire job queues (recommended when wiping runs)"
              checked={clearHangfire}
              disabled={busy}
              onChange={(e) => setClearHangfire(e.target.checked)}
            />

            {scope === 'full' && (
              <Form.Group className="mb-3">
                <Form.Label>Type <code className="small">{FULL_APPLICATION_DATA_CONFIRM_PHRASE}</code> to confirm full wipe</Form.Label>
                <Form.Control
                  value={confirmPhrase}
                  onChange={(e) => setConfirmPhrase(e.target.value)}
                  placeholder={FULL_APPLICATION_DATA_CONFIRM_PHRASE}
                  disabled={busy}
                  autoComplete="off"
                />
              </Form.Group>
            )}

            <Button variant="danger" disabled={busy} onClick={() => void handleReset()}>
              {busy ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Working…
                </>
              ) : (
                'Execute reset'
              )}
            </Button>
          </Form>
        </Card.Body>
      </Card>

      <Alert variant="light" className="small border border-secondary-subtle bg-white">
        Successful resets append a{' '}
        <code className="small">Maintenance.FullDataReset</code> audit entry after clearing (unless the HTTP call failed before SaveChanges completed).
      </Alert>
    </Container>
  )
}
